import { useIsExperimentsReady } from '@seek/candidate-feature-flag';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { PageName } from '@seek/sol-js';
import { useTranslations } from '@vocab/react';
import { ContentBlock, Page, PageBlock } from 'braid-design-system';
import { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import AnalyticsContext from 'src/analytics/AnalyticsContext';
import type { RecommendedJobsAnalytics } from 'src/analytics/adapter/recommendedJobs';
import { SolContextProvider } from 'src/context/solContext/solContext';
import { useSetRecsExperiments } from 'src/hooks/experiments/useSetExperiments';
import useHubbleTrackDisplayEvent from 'src/hooks/useHubbleTrackDisplayEvent.ts';
import { ContainerWidth } from 'src/types';
import { brandToCaseMap } from 'src/utils/brand';

import useRecommendations from '../../hooks/useRecommendations';
import { UserProvider } from '../App/UserContext';
import { useAppConfig } from '../App/useAppConfig';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import { ScreenReaderSkipLink } from '../ScreenReaderSkipLink/ScreenReaderSkipLink';
import TitleSection from '../TitleSection/TitleSection';

import translations from './.vocab';
import RecommendationsContent from './RecommendationsContent/RecommendationsContent';

const Container = () => {
  const { t } = useTranslations(translations);
  const { language, country, brand, zone } = useAppConfig();

  const {
    userData,
    loading,
    error,
    solMetadata,
    globalJobs,
    jobCount,
    hasAuthError,
    hasError,
  } = useRecommendations({ language, zone });
  const { firstName, emailAddress } = userData;

  const { recommendedJobsPageLoaded } = useContext(
    AnalyticsContext,
  ) as RecommendedJobsAnalytics;

  useSetRecsExperiments();
  const isExperimentsReady = useIsExperimentsReady();

  useEffect(() => {
    metrics.count('pageLoad', [`splitview:${true}`]);
    if (!loading && isExperimentsReady) {
      recommendedJobsPageLoaded({
        brand,
        currentPage: 'recommended jobs',
        jobsCount: jobCount?.toString() || '',
        loginId: userData?.id?.toString() || '',
        profileGuid: userData?.trackingId || '',
        siteCountry: country.toLowerCase(),
      });
    }
  }, [
    brand,
    country,
    isExperimentsReady,
    jobCount,
    loading,
    recommendedJobsPageLoaded,
    userData?.id,
    userData?.trackingId,
  ]);
  useHubbleTrackDisplayEvent({ solMetadata, page: PageName.RECOMMENDATIONS });

  const { hostname, pathWithoutLanguage } = useMelwaysInfo();
  const href = `https://${hostname}${
    language !== 'en' ? `/${language}` : ''
  }${pathWithoutLanguage}`;

  return (
    <>
      <Helmet
        title={t('{brand} - Recommended Job', { brand: brandToCaseMap[brand] })}
        link={[{ rel: 'canonical', href }]}
      />
      <UserProvider value={userData}>
        <ScreenReaderSkipLink />
        <Page
          footerPosition="belowFold"
          footer={
            <Footer
              width={ContainerWidth.Large}
              loading={loading}
              error={error}
            />
          }
        >
          <Header
            firstName={firstName}
            emailAddress={emailAddress}
            loading={loading}
            error={error}
            width={ContainerWidth.Large}
            activeAuthedUserTab="recommendedJobs"
          />
          <PageBlock width="large">
            <ContentBlock width="large">
              <TitleSection title={t('Recommended jobs')} />
              <SolContextProvider>
                <RecommendationsContent
                  globalJobs={globalJobs}
                  jobCount={jobCount}
                  hasAuthError={hasAuthError}
                  hasError={hasError}
                  loading={loading}
                />
              </SolContextProvider>
            </ContentBlock>
          </PageBlock>
        </Page>
      </UserProvider>
    </>
  );
};

export default Container;
